import { isAxiosError } from "axios";
import { ReportJobStatus } from "../../shared/interfaces/JobStatus";
import type { Job } from "../../store/jobStore";
import type { JobStatusSnapshot } from "../../features/jobs/api/jobStatusApi";
import { isWithinResumeGrace } from "../../features/jobs/api/jobQueries";

export type GenerationView =
  | { kind: "loading" }
  | {
      kind: "progress";
      progress: number;
      message?: string;
      videoTitles?: string[];
      videoCount?: number;
      // Narrative-only generation has no meaningful percentage — the page
      // renders a 2-minute countdown anchored at `startedAt` instead.
      narrativeOnly?: boolean;
      startedAt?: Date | string | number;
    }
  | { kind: "awaiting-input"; targetId: string }
  | {
      kind: "transcript-decision";
      jobId: string;
      reportId: string;
      failedVideoIds: string[];
      message?: string;
    }
  | { kind: "ready"; reportId: string }
  | { kind: "failed"; message: string; canRetry: boolean }
  | { kind: "unknown" };

const RUNNING_STATUSES = new Set<string>([
  ReportJobStatus.PENDING,
  ReportJobStatus.STARTED,
  ReportJobStatus.GENERATING,
  ReportJobStatus.EXTRACTING,
]);

/**
 * Pure view derivation for the generation surface. The /jobStatus snapshot
 * is authoritative for status/progress; the (per-device) registry entry only
 * enriches the view (video titles, retryability) — a job started on another
 * device still renders read-only from the snapshot alone.
 */
export function deriveGenerationView(
  registryJob: Job | undefined,
  snapshot: JobStatusSnapshot | undefined,
  error: unknown,
  /**
   * When the officer just answered a human-input prompt. Defaults to the
   * registry entry's own stamp; callers pass the store's jobId-keyed ledger so
   * the grace window also covers a job this device never registered.
   */
  resumedAt: number | undefined = registryJob?.resumedAt,
): GenerationView {
  // A hard HTTP error is authoritative even when a previous snapshot is
  // retained in the cache (react-query keeps data through error dispatches);
  // without this the page would show eternal progress off the stale snapshot.
  const hardHttpError = !!error && isAxiosError(error) && !!error.response;
  if (!snapshot || hardHttpError) {
    if (error) {
      if (isAxiosError(error) && error.response) {
        // The server doesn't know this job (or rejected it): a stale link or
        // another device's registry — unless WE know it failed locally.
        if (registryJob?.status === ReportJobStatus.FAILED) {
          return {
            kind: "failed",
            message: registryJob.error || "Video processing failed.",
            canRetry: true,
          };
        }
        return { kind: "unknown" };
      }
      // Network blip: keep showing what we know; the poll rides through.
      if (registryJob) {
        return progressView(registryJob.progress, registryJob);
      }
      return { kind: "loading" };
    }
    // No snapshot yet. A locally-FAILED job has no active query to deliver
    // one (the fan-out only polls active jobs), so derive from the registry.
    if (registryJob?.status === ReportJobStatus.FAILED) {
      return {
        kind: "failed",
        message: registryJob.error || "Video processing failed.",
        canRetry: true,
      };
    }
    if (registryJob?.status === ReportJobStatus.PENDING_REVIEW) {
      return registryJob.reportId
        ? { kind: "ready", reportId: registryJob.reportId }
        : { kind: "loading" };
    }
    return { kind: "loading" };
  }

  const status = snapshot.jobStatus;
  // Just resumed after human input: the backend can still report AWAITING_*
  // for a beat before the graph picks the resume up. Show the loading screen
  // during the grace window instead of bouncing back to the input CTA the
  // user already acted on (the poll keeps running to catch the flip to a
  // running status — see jobStatusPollInterval).
  const awaitingHuman =
    status === ReportJobStatus.AWAITING_TRANSCRIPT_DECISION ||
    status === ReportJobStatus.AWAITING_INPUT;
  if (awaitingHuman && isWithinResumeGrace(resumedAt)) {
    return { kind: "loading" };
  }
  if (status === ReportJobStatus.AWAITING_TRANSCRIPT_DECISION) {
    return {
      kind: "transcript-decision",
      jobId: snapshot.jobId,
      reportId: snapshot.reportId ?? registryJob?.reportId ?? snapshot.jobId,
      failedVideoIds: snapshot.failedTranscriptionVideoIds ?? [],
      message: snapshot.message,
    };
  }
  if (status === ReportJobStatus.AWAITING_INPUT) {
    return {
      kind: "awaiting-input",
      targetId: snapshot.reportId ?? registryJob?.reportId ?? snapshot.jobId,
    };
  }
  if (status === ReportJobStatus.PENDING_REVIEW) {
    const reportId = snapshot.reportId ?? registryJob?.reportId;
    return reportId ? { kind: "ready", reportId } : { kind: "loading" };
  }
  if (status === ReportJobStatus.FAILED) {
    return {
      kind: "failed",
      message:
        snapshot.message ||
        registryJob?.error ||
        "Video processing failed.",
      canRetry: !!registryJob,
    };
  }
  if (RUNNING_STATUSES.has(status)) {
    return progressView(snapshot.progress ?? registryJob?.progress ?? 0, registryJob, snapshot.message);
  }
  // Unknown status: render it as progress rather than an error — matches the
  // poll predicate, which keeps polling unknown statuses.
  return progressView(snapshot.progress ?? registryJob?.progress ?? 0, registryJob, snapshot.message);
}

function progressView(
  progress: number,
  registryJob: Job | undefined,
  message?: string,
): GenerationView {
  return {
    kind: "progress",
    progress,
    message: message || registryJob?.progressMessage,
    videoTitles: registryJob?.videoTitles,
    videoCount: registryJob?.evidenceIDs.length,
    narrativeOnly: registryJob?.narrativeOnly,
    startedAt: registryJob?.displayStartedAt ?? registryJob?.createdAt,
  };
}
